import { useEffect } from "react";
import { createPortal } from "react-dom";

interface ErrorToastProps {
  message: string | null;
  onDismiss: () => void;
  duration?: number;
}

/**
 * Transient, auto-dismissing error toast pinned to the bottom-right of the
 * viewport. Renders nothing when `message` is null. Portaled to the body, so
 * a filtered or transformed ancestor can't become its containing block.
 */
export function ErrorToast({
  message,
  onDismiss,
  duration = 5000,
}: ErrorToastProps) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [message, duration, onDismiss]);

  if (!message) return null;

  return createPortal(
    <div
      role="alert"
      className="fixed bottom-6 right-6 z-50 flex max-w-sm items-start gap-3 rounded-lg border border-[var(--stroke-destructive)] bg-[var(--accent-destructive)] p-4 shadow-lg"
    >
      <svg
        className="h-5 w-5 flex-shrink-0 text-[var(--text-primary)]"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-label="Error"
        role="img"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
          clipRule="evenodd"
        />
      </svg>
      <p className="flex-1 text-sm text-[var(--text-primary)]">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
